import {
  CLOSED_DEALS_KPI,
  DEAL_ACTIVITY_KPI,
  MONTHLY_OPEN_VOLUME_KPI,
  OPEN_CUSTOMER_OPPS_KPI,
  REP_ACTIVITY_KPI,
  REP_CLOSED_DEALS_KPI,
  REP_OPEN_CUSTOMER_OPPS_KPI,
  REP_OPEN_VOLUME_KPI,
  REP_VOLUME_KPI,
  REP_WON_VOLUME_KPI,
  STARTER_KPI,
  WON_VOLUME_KPI,
} from './kpis'
import { CURRENT_REP } from './persona'
import { ViewConfig } from './view-config'

/** Scope a report to the logged-in rep — the base filter knobs never touch. */
const MINE: ViewConfig['filters'][number] = {
  field: 'account_owner',
  op: 'in',
  value: [CURRENT_REP],
}

/** Sales stages that count as "open pipeline" (not yet closed). */
const OPEN_STAGES = ['Developing', 'Finalized Pricing', 'Interested', 'Target']

/** Created in the last year and closed in 2026 — the TM "won this year" window. */
const CLOSED_THIS_YEAR: ViewConfig['filters'] = [
  { field: 'created', op: 'within_days', value: 365 },
  { field: 'actual_close_date', op: 'date_after', value: '2026-01-01' },
]

/** Closed-won and closed in 2026 — the by-rep "won this year" window. */
const CLOSED_WON_2026: ViewConfig['filters'] = [
  { field: 'sales_stage', op: 'in', value: ['Closed Won'] },
  { field: 'actual_close_date', op: 'date_after', value: '2026-01-01' },
]

/** The deal-report filter knobs shared across the TM Admin reports. */
const DEAL_KNOBS: ViewConfig['knobs'] = [
  { type: 'filter-select', field: 'sales_stage', label: 'Sales Stage' },
  { type: 'filter-select', field: 'account_owner', label: 'Account Owner' },
  { type: 'filter-select', field: 'category', label: 'Type' },
]

/** Knobs shared across the deal_activity-frame TM reports. */
const ACTIVITY_KNOBS: ViewConfig['knobs'] = [
  { type: 'filter-select', field: 'event_type', label: 'Activity' },
  { type: 'filter-select', field: 'owner', label: 'Owner' },
  { type: 'filter-select', field: 'deal_name', label: 'Deal' },
]

/** Rep-scoped deal knobs — no owner knob (the report is already scoped to MINE). */
const REP_DEAL_KNOBS: ViewConfig['knobs'] = [
  { type: 'filter-select', field: 'sales_stage', label: 'Sales Stage' },
  { type: 'filter-select', field: 'category', label: 'Type' },
]

/** Rep-scoped activity knobs — no owner knob (already scoped to CURRENT_REP). */
const REP_ACTIVITY_KNOBS: ViewConfig['knobs'] = [
  { type: 'filter-select', field: 'event_type', label: 'Activity' },
  { type: 'filter-select', field: 'deal_name', label: 'Deal' },
]

/** deal_activity owner scoped to the logged-in rep — the activity-frame MINE. */
const MINE_ACTIVITY: ViewConfig['filters'][number] = {
  field: 'owner',
  op: 'in',
  value: [CURRENT_REP],
}

/** Deal-activity event types that count (excludes raw email). */
const ACTIVITY_EVENTS = ['added_contact', 'added_doc', 'added_note', 'had_meeting', 'updated_sales_stage', 'had_call'] // prettier-ignore

/**
 * A report goal linked to a saved KPI. The `value`/`dir` here are a stale
 * fallback — both are resolved from the live KPI at render via `resolveThreshold`
 * (kpis.ts), so editing the KPI moves every report linked to it.
 */
const kpiGoal = (kpi: {
  id: string
  value: number
  dir?: 'above' | 'below'
}): ViewConfig['threshold'] => ({
  value: kpi.value,
  dir: kpi.dir ?? 'above',
  kpiId: kpi.id,
})

/**
 * The shipped report templates. Pure data — adding a template here is the
 * only step needed to ship a new report. Knobs whitelist what users may
 * change per template (the rung-2 validity guard).
 *
 * The team-wide (admin) set is deliberately one report PER TYPE — a gallery of
 * the shapes the builder can produce (metric, KPI, bar, time series, time
 * series split, table) rather than an exhaustive report list. Between them they
 * also exercise every builder knob: filtering, sorting, column-select and limit
 * (the table), filter-selects (metric/bar/time series), and a KPI-linked target
 * (the KPI report tracks a saved KPI, not a hardcoded number). The `icon` keys
 * off each report's *type* (see reportIcons.ts).
 *
 * The rep-tagged (`personas: ['rep']`) reports below are a separate curated
 * view for the Sales Rep persona, scoped to CURRENT_REP — not part of the
 * type gallery.
 */
export const TEMPLATES: ViewConfig[] = [
  // ── Type gallery (team-wide / admin) ─────────────────────────────────────
  {
    // Metric: an aggregate with NO groupBy resolves to one number.
    id: 'total-deals',
    icon: 'metric',
    name: 'Total Deals',
    description: 'How many deals are in the pipeline right now.',
    kind: 'aggregate',
    filters: [],
    metrics: [{ agg: 'count', label: 'Deals' }],
    chartMetric: 'Deals',
    chart: 'table-only',
    knobs: [
      { type: 'filter-select', field: 'account_owner', label: 'Owner' },
      { type: 'filter-select', field: 'sales_stage', label: 'Stage' },
    ],
  },
  {
    // KPI: a metric tracked against a *saved KPI* — the target value lives in
    // the KPI store, not on the report. `threshold.kpiId` links it; the value
    // is resolved from the live KPI at render (see resolveThreshold), so editing
    // the "Quarterly revenue target" KPI moves this report. The literal `value`
    // here is only a stale fallback for when the KPI is missing.
    id: 'closed-revenue-vs-target',
    icon: 'kpi',
    name: 'Closed Revenue vs Target',
    description: 'Closed-won revenue against the quarterly revenue KPI.',
    kind: 'aggregate',
    filters: [{ field: 'sales_stage', op: 'eq', value: 'Closed Won' }],
    metrics: [{ agg: 'sum', field: 'annual_revenue', label: 'Yearly Revenue' }],
    chartMetric: 'Yearly Revenue',
    chart: 'table-only',
    threshold: kpiGoal(STARTER_KPI),
    knobs: [
      { type: 'filter-select', field: 'account_owner', label: 'Owner' },
      { type: 'filter-select', field: 'category', label: 'Opp type' },
    ],
  },
  {
    // Bar: a categorical aggregate, one bar per group.
    id: 'pipeline-by-stage',
    icon: 'chart',
    name: 'Pipeline by Stage',
    description: 'Deal count at each sales stage.',
    kind: 'aggregate',
    filters: [],
    groupBy: { field: 'sales_stage' },
    metrics: [
      { agg: 'count', label: 'Deals' },
      { agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' },
      { agg: 'sum', field: 'monthly_revenue', label: 'Monthly Revenue' },
      { agg: 'sum', field: 'annual_volume', label: 'Yearly Volume' },
      { agg: 'sum', field: 'annual_revenue', label: 'Yearly Revenue' },
    ],
    chartMetric: 'Deals',
    chart: 'bar',
    knobs: [
      { type: 'filter-select', field: 'category', label: 'Opp type' },
      { type: 'filter-select', field: 'account_owner', label: 'Owner' },
    ],
  },
  {
    // Time series: an aggregate bucketed by month, one point per bucket.
    id: 'deals-created-monthly',
    icon: 'line',
    name: 'Deals Created per Month',
    description: 'New deals created each month.',
    kind: 'aggregate',
    filters: [],
    groupBy: { field: 'created', bucket: 'month' },
    metrics: [{ agg: 'count', label: 'Deals' }],
    chartMetric: 'Deals',
    chart: 'line',
    knobs: [
      { type: 'filter-select', field: 'account_owner', label: 'Owner' },
      { type: 'filter-select', field: 'category', label: 'Opp type' },
    ],
  },
  {
    // Projected pipeline: open deals bucketed by their *projected* close month,
    // valued at probability-weighted revenue (expected = potential × stage win
    // rate). Because projected_close_date = now + the stage's days-to-close, the
    // line runs forward into future months — a forecast, not history.
    id: 'projected-pipeline',
    icon: 'line',
    name: 'Projected Pipeline by Month',
    description:
      'Probability-weighted revenue of open deals by projected close month.',
    kind: 'aggregate',
    filters: [{ op: 'is_open' }],
    groupBy: { field: 'projected_close_date', bucket: 'month' },
    metrics: [
      { agg: 'sum', field: 'forecasted_annual_revenue', label: 'Forecasted Yearly Revenue' }, // prettier-ignore
      { agg: 'sum', field: 'annual_revenue', label: 'Yearly Revenue' },
      { agg: 'sum', field: 'forecasted_monthly_volume', label: 'Forecasted Monthly Volume' }, // prettier-ignore
    ],
    chartMetric: 'Forecasted Yearly Revenue',
    chart: 'line',
    knobs: [
      { type: 'filter-select', field: 'sales_stage', label: 'Stage' },
      { type: 'filter-select', field: 'account_owner', label: 'Owner' },
      { type: 'filter-select', field: 'category', label: 'Opp type' },
    ],
  },
  {
    // Closed-won deals per rep, created since 2026: a categorical bar (count by
    // rep), not a time-series split.
    id: 'deals-created-by-rep',
    icon: 'chart',
    name: 'Deals Created by Rep',
    description: 'Closed-won deals per rep, created since 2026.',
    kind: 'aggregate',
    filters: [
      { field: 'sales_stage', op: 'eq', value: 'Closed Won' },
      { field: 'created', op: 'date_after', value: '2026-01-01' },
    ],
    groupBy: { field: 'account_owner' },
    metrics: [{ agg: 'count', label: 'Deals' }],
    chartMetric: 'Deals',
    chart: 'bar',
    knobs: [{ type: 'filter-select', field: 'category', label: 'Opp type' }],
  },
  {
    // Table: a raw row list. Exercises sort, column-select and limit knobs.
    id: 'top-by-volume',
    icon: 'table',
    name: 'Top Deals by Volume',
    description: 'Biggest deals by monthly volume (excludes closed-lost).',
    kind: 'list',
    filters: [{ field: 'sales_stage', op: 'neq', value: 'Closed Lost' }],
    columns: [
      'account_owner',
      'company_name',
      'category',
      'product',
      'sales_stage',
      'projected_close_date',
      'monthly_volume',
      'annual_volume',
      'monthly_revenue',
      'annual_revenue',
    ],
    sort: { by: 'monthly_volume', dir: 'desc' },
    limit: 10,
    chart: 'table-only',
    knobs: [
      { type: 'filter-select', field: 'category', label: 'Opp type' },
      { type: 'filter-select', field: 'sales_stage', label: 'Stage' },
      { type: 'filter-select', field: 'account_owner', label: 'Owner' },
      { type: 'column-picker' },
      { type: 'limit', presets: [5, 10, 25] },
    ],
  },

  // ── TM Admin Dashboard reports (team-wide / admin) ───────────────────────
  // The report set composed by the "TM Admin Dashboard" (dashboards.ts). Every
  // goal links a saved KPI (kpis.ts) via threshold.kpiId — editing the KPI moves
  // the report; the literal `value` is only a stale fallback (see resolveThreshold).
  {
    id: 'tm-open-monthly-volume',
    name: 'Open Volume LBS',
    description:
      'Open-pipeline monthly volume against the monthly open-volume goal.',
    kind: 'aggregate',
    filters: [{ field: 'sales_stage', op: 'in', value: OPEN_STAGES }],
    metrics: [{ agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' }],
    chartMetric: 'Monthly Volume',
    chart: 'table-only',
    threshold: kpiGoal(MONTHLY_OPEN_VOLUME_KPI),
    knobs: DEAL_KNOBS,
  },
  {
    id: 'tm-open-customer-opps',
    name: 'Open Customer Opps',
    description: 'Distinct companies with an open opportunity.',
    kind: 'aggregate',
    filters: [{ field: 'sales_stage', op: 'in', value: OPEN_STAGES }],
    metrics: [
      { agg: 'distinct', field: 'company_name', label: 'Distinct Company' },
    ],
    chartMetric: 'Distinct Company',
    chart: 'table-only',
    threshold: kpiGoal(OPEN_CUSTOMER_OPPS_KPI),
    knobs: DEAL_KNOBS,
  },
  {
    id: 'tm-deals-volume-won-this-year',
    name: 'Deal LBS Won 2026',
    description:
      'Monthly volume of deals created in the last year and closed in 2026.',
    kind: 'aggregate',
    filters: CLOSED_THIS_YEAR,
    metrics: [{ agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' }],
    chartMetric: 'Monthly Volume',
    chart: 'table-only',
    // Shares the "Annual won volume target" KPI with tm-closed-volume-2026.
    threshold: kpiGoal(WON_VOLUME_KPI),
    knobs: DEAL_KNOBS,
  },
  {
    id: 'tm-deals-won-this-year',
    name: 'Deals Won 2026',
    description: 'Count of deals won (closed in 2026).',
    kind: 'aggregate',
    filters: [
      { field: 'actual_close_date', op: 'date_after', value: '2026-01-01' },
    ],
    metrics: [{ agg: 'sum', field: 'is_won', label: 'Won' }],
    chartMetric: 'Won',
    chart: 'table-only',
    // Shares the "Closed deals goal" KPI with tm-closed-deals-2026.
    threshold: kpiGoal(CLOSED_DEALS_KPI),
    knobs: DEAL_KNOBS,
  },
  {
    id: 'tm-closed-deals-2026',
    name: 'Deals Won 2026 (Running)',
    description: 'Running count of closed-won deals in 2026.',
    kind: 'aggregate',
    filters: CLOSED_WON_2026,
    groupBy: { field: 'actual_close_date', bucket: 'month' },
    metrics: [{ agg: 'count', label: 'Deals' }],
    chartMetric: 'Deals',
    chart: 'line',
    cumulative: true,
    threshold: kpiGoal(CLOSED_DEALS_KPI),
    knobs: DEAL_KNOBS,
  },
  {
    id: 'tm-closed-volume-2026',
    name: 'Closed Volume LBS 2026',
    description: 'Running closed-won monthly volume (LBS) in 2026.',
    kind: 'aggregate',
    filters: CLOSED_WON_2026,
    groupBy: { field: 'actual_close_date', bucket: 'month' },
    metrics: [{ agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' }],
    chartMetric: 'Monthly Volume',
    chart: 'line',
    cumulative: true,
    // Shares the "Annual won volume target" KPI with tm-deals-volume-won-this-year.
    threshold: kpiGoal(WON_VOLUME_KPI),
    knobs: DEAL_KNOBS,
  },
  {
    id: 'tm-deals-won-by-rep-2026',
    name: 'Deals Won by Rep 2026',
    description: 'Closed-won deal count per rep in 2026.',
    kind: 'aggregate',
    filters: CLOSED_WON_2026,
    groupBy: { field: 'account_owner' },
    metrics: [{ agg: 'count', label: 'Deals' }],
    chartMetric: 'Deals',
    chart: 'horizontal-bar',
    knobs: DEAL_KNOBS,
  },
  {
    id: 'tm-won-lbs-by-rep-2026',
    name: 'Won LBS by Rep 2026',
    description: 'Closed-won monthly volume (LBS) per rep in 2026.',
    kind: 'aggregate',
    filters: CLOSED_WON_2026,
    groupBy: { field: 'account_owner' },
    metrics: [{ agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' }],
    chartMetric: 'Monthly Volume',
    chart: 'horizontal-bar',
    threshold: kpiGoal(REP_VOLUME_KPI),
    knobs: DEAL_KNOBS,
  },
  {
    id: 'tm-monthly-deal-activity',
    name: 'Monthly Deal Activity',
    description: 'Logged deal activity per month (excludes email).',
    kind: 'aggregate',
    frame: 'deal_activity',
    filters: [{ field: 'event_type', op: 'in', value: ACTIVITY_EVENTS }],
    groupBy: { field: 'created_at', bucket: 'month' },
    metrics: [{ agg: 'count', label: 'Activities' }],
    chartMetric: 'Activities',
    chart: 'dot',
    threshold: kpiGoal(DEAL_ACTIVITY_KPI),
    knobs: ACTIVITY_KNOBS,
  },
  {
    id: 'tm-deal-activity-by-rep-2026',
    name: 'Deal Activity by Rep 2026',
    description:
      'Calls, notes and deal motion per rep in 2026 (excludes email).',
    kind: 'aggregate',
    frame: 'deal_activity',
    filters: [
      { field: 'event_type', op: 'in', value: ACTIVITY_EVENTS },
      { field: 'created_at', op: 'date_after', value: '2026-01-01' },
    ],
    groupBy: { field: 'owner' },
    metrics: [{ agg: 'count', label: 'Activities' }],
    chartMetric: 'Activities',
    chart: 'horizontal-bar',
    knobs: ACTIVITY_KNOBS,
  },

  // ── TM Rep Dashboard reports — the admin TM set scoped to CURRENT_REP ──────
  // Mirror the team-wide TM metrics for a single rep (MINE filter), reusing the
  // same goal KPIs so a rep grades against the team target. The by-rep
  // comparison charts are dropped (meaningless for one rep); the rep dashboard
  // pairs these with the shared my-stale-deals / my-open-deals tables instead.
  {
    id: 'tm-rep-open-monthly-volume',
    name: 'My Open Volume LBS',
    description:
      'My open-pipeline monthly volume against the open-volume goal.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE, { field: 'sales_stage', op: 'in', value: OPEN_STAGES }],
    metrics: [{ agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' }],
    chartMetric: 'Monthly Volume',
    chart: 'table-only',
    threshold: kpiGoal(REP_OPEN_VOLUME_KPI),
    knobs: REP_DEAL_KNOBS,
  },
  {
    id: 'tm-rep-open-customer-opps',
    name: 'My Open Customer Opps',
    description: 'Distinct companies with an open opportunity of mine.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE, { field: 'sales_stage', op: 'in', value: OPEN_STAGES }],
    metrics: [
      { agg: 'distinct', field: 'company_name', label: 'Distinct Company' },
    ],
    chartMetric: 'Distinct Company',
    chart: 'table-only',
    threshold: kpiGoal(REP_OPEN_CUSTOMER_OPPS_KPI),
    knobs: REP_DEAL_KNOBS,
  },
  {
    id: 'tm-rep-deal-lbs-won-2026',
    name: 'My Deal LBS Won 2026',
    description:
      'Monthly volume of my deals created in the last year and closed in 2026.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE, ...CLOSED_THIS_YEAR],
    metrics: [{ agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' }],
    chartMetric: 'Monthly Volume',
    chart: 'table-only',
    threshold: kpiGoal(REP_WON_VOLUME_KPI),
    knobs: REP_DEAL_KNOBS,
  },
  {
    id: 'tm-rep-deals-won-2026',
    name: 'My Deals Won 2026',
    description: 'Count of my deals won (closed in 2026).',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [
      MINE,
      { field: 'actual_close_date', op: 'date_after', value: '2026-01-01' },
    ],
    metrics: [{ agg: 'sum', field: 'is_won', label: 'Won' }],
    chartMetric: 'Won',
    chart: 'table-only',
    threshold: kpiGoal(REP_CLOSED_DEALS_KPI),
    knobs: REP_DEAL_KNOBS,
  },
  {
    id: 'tm-rep-deals-won-running',
    name: 'My Deals Won 2026 (Running)',
    description: 'Running count of my closed-won deals in 2026.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE, ...CLOSED_WON_2026],
    groupBy: { field: 'actual_close_date', bucket: 'month' },
    metrics: [{ agg: 'count', label: 'Deals' }],
    chartMetric: 'Deals',
    chart: 'line',
    cumulative: true,
    threshold: kpiGoal(REP_CLOSED_DEALS_KPI),
    knobs: REP_DEAL_KNOBS,
  },
  {
    id: 'tm-rep-closed-volume-2026',
    name: 'My Closed Volume LBS 2026',
    description: 'Running closed-won monthly volume (LBS) of mine in 2026.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE, ...CLOSED_WON_2026],
    groupBy: { field: 'actual_close_date', bucket: 'month' },
    metrics: [{ agg: 'sum', field: 'monthly_volume', label: 'Monthly Volume' }],
    chartMetric: 'Monthly Volume',
    chart: 'line',
    cumulative: true,
    threshold: kpiGoal(REP_WON_VOLUME_KPI),
    knobs: REP_DEAL_KNOBS,
  },
  {
    id: 'tm-rep-monthly-deal-activity',
    name: 'My Monthly Deal Activity',
    description: 'My logged deal activity per month (excludes email).',
    kind: 'aggregate',
    personas: ['rep'],
    frame: 'deal_activity',
    filters: [
      MINE_ACTIVITY,
      { field: 'event_type', op: 'in', value: ACTIVITY_EVENTS },
    ],
    groupBy: { field: 'created_at', bucket: 'month' },
    metrics: [{ agg: 'count', label: 'Activities' }],
    chartMetric: 'Activities',
    chart: 'dot',
    threshold: kpiGoal(REP_ACTIVITY_KPI),
    knobs: REP_ACTIVITY_KNOBS,
  },

  // ── Rep persona — own deals, quota, staleness (scoped to CURRENT_REP) ──────
  {
    id: 'my-quota',
    icon: 'kpi',
    name: 'My Quota Attainment',
    description: 'Closed-won revenue against my target.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE, { field: 'sales_stage', op: 'eq', value: 'Closed Won' }],
    metrics: [{ agg: 'sum', field: 'annual_revenue', label: 'Yearly Revenue' }],
    chartMetric: 'Yearly Revenue',
    chart: 'table-only',
    threshold: { value: 5_000_000, label: 'Quota', dir: 'above' },
    knobs: [],
  },
  {
    id: 'my-win-rate',
    icon: 'metric',
    name: 'My Win Rate',
    description: 'Share of my closed deals won.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE],
    metrics: [{ agg: 'avg', field: 'is_won', label: 'Win Rate' }],
    chartMetric: 'Win Rate',
    chart: 'table-only',
    threshold: { value: 0.5, label: 'Target', dir: 'above' },
    knobs: [],
  },
  {
    id: 'my-open-pipeline',
    icon: 'metric',
    name: 'My Open Pipeline',
    description: 'Total annual revenue across my open deals.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE, { op: 'is_open' }],
    metrics: [{ agg: 'sum', field: 'annual_revenue', label: 'Yearly Revenue' }],
    chartMetric: 'Yearly Revenue',
    chart: 'table-only',
    knobs: [{ type: 'filter-select', field: 'category', label: 'Opp type' }],
  },
  {
    id: 'my-pipeline-by-stage',
    icon: 'chart',
    name: 'My Pipeline by Stage',
    description: 'My deal count at each sales stage.',
    kind: 'aggregate',
    personas: ['rep'],
    filters: [MINE],
    groupBy: { field: 'sales_stage' },
    metrics: [
      { agg: 'count', label: 'Deals' },
      { agg: 'sum', field: 'annual_revenue', label: 'Yearly Revenue' },
    ],
    chartMetric: 'Deals',
    chart: 'bar',
    knobs: [{ type: 'filter-select', field: 'category', label: 'Opp type' }],
  },
  {
    id: 'my-open-deals',
    icon: 'table',
    name: 'My Open Deals',
    description: 'My open deals, biggest first.',
    kind: 'list',
    personas: ['rep'],
    filters: [MINE, { op: 'is_open' }],
    columns: [
      'company_name',
      'category',
      'product',
      'sales_stage',
      'days_in_stage',
      'days_since_update',
      'projected_close_date',
      'monthly_volume',
      'annual_revenue',
    ],
    sort: { by: 'annual_revenue', dir: 'desc' },
    limit: 25,
    chart: 'table-only',
    knobs: [
      { type: 'filter-select', field: 'sales_stage', label: 'Stage' },
      { type: 'filter-select', field: 'category', label: 'Opp type' },
      { type: 'column-picker' },
      { type: 'limit', presets: [10, 25, 0] },
    ],
  },
  {
    id: 'my-stale-deals',
    icon: 'table',
    name: 'My Stale Deals',
    description: 'My open deals with no activity in 30+ days.',
    kind: 'list',
    personas: ['rep'],
    filters: [
      MINE,
      { op: 'is_open' },
      { field: 'days_since_update', op: 'gte', value: 30 },
    ],
    columns: [
      'company_name',
      'sales_stage',
      'days_since_update',
      'days_in_stage',
      'projected_close_date',
      'annual_revenue',
    ],
    sort: { by: 'days_since_update', dir: 'desc' },
    limit: 25,
    chart: 'table-only',
    knobs: [{ type: 'limit', presets: [10, 25, 0] }],
  },
]

export const getTemplate = (id: string): ViewConfig | undefined =>
  TEMPLATES.find((t) => t.id === id)
